/**
 * Shared number / cell formatting utilities.
 */
import React from "react";
import type { FormatNegativeOpts } from "../types/api";

/**
 * Format a number showing negatives in red with parentheses.
 *   formatNegativeValue(-1234)                                 => <span>(1,234)</span>
 *   formatNegativeValue(0.753, {isPercent:true, decimals:1})   => "75.3%"
 */
export const formatNegativeValue = (
  value: number | null | undefined,
  { isPercent = false, decimals = 0 }: FormatNegativeOpts = {},
): React.ReactNode => {
  if (value === null || value === undefined) return "\u2014";

  const n = Number(value);
  if (!Number.isFinite(n)) return "\u2014";

  const opts: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };
  const suffix = isPercent ? "%" : "";

  if (n < 0) {
    return (
      <span style={{ color: "red" }}>
        ({Math.abs(n).toLocaleString(undefined, opts)}){suffix}
      </span>
    );
  }

  return `${n.toLocaleString(undefined, opts)}${suffix}`;
};

/**
 * Shorthand cell formatter (0 dp, no percent). Handles null gracefully.
 */
export const formatCell = (value: number | null | undefined): React.ReactNode => {
  if (value == null) return "";
  return formatNegativeValue(value);
};

/**
 * Abbreviate large numbers: 1 234 000 => "1.23M", 5 600 => "5.60k".
 */
export const numberFmt = (n: number | null | undefined): string => {
  if (n == null) return "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + "B";
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (abs >= 1_000) return (n / 1_000).toFixed(2) + "k";
  return String(n);
};

/**
 * Format a number with thousand separators, negatives in parentheses (string output).
 */
export const fmtNum = (v: number | string | null | undefined): string => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n < 0 ? `(${abs})` : abs;
};

/**
 * Format a volatility / percentage move with 1 dp, e.g. "12.3%".
 */
export const fmtVolMove = (v: number | string | null | undefined): string => {
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toFixed(1)}%` : "";
};
